/**
 * This app's RBAC extension point — the ONE file to edit when this app needs
 * its own `ng.*` permission resources (the shared `ng.users` / `ng.roles`
 * catalogue itself lives in `@trueform/ui/core` and is not copied here).
 *
 * Adding a resource takes TWO halves, and both start here:
 *
 *  1. THE TYPE HALF — augment the library's open `TrueformResourceActions`
 *     interface (the `declare module` block below). Compile-time only: it
 *     widens `AppResource`, `AppPermission`, `PermissionTuple`, … so
 *     `*hasPermission="['ng.orders', 'view']"`, `requirePermission(...)`, and
 *     `authService.hasPermission(...)` accept the new pairs with the same
 *     strict resource↔action checking as the library's own — and reject
 *     typos.
 *  2. THE RUNTIME HALF — `provideAuthCatalog(APP_RESOURCE_ACTIONS)` in
 *     `app.config.ts` merges this file's catalogue over the library baseline
 *     for the DI'd `AUTH_CATALOG` token, which feeds
 *     `AppAuthService.assignablePermissions` (the roles screen's grant list)
 *     and the E2E-mode default grant.
 *
 * Why both: augmentation cannot create a runtime object, and DI cannot widen
 * a TypeScript union. Do only the first and the app compiles but the runtime
 * catalogue never lists your permissions (an E2E session isn't granted them,
 * a permissions picker shows nothing). Do only the second and the catalogue
 * ships at runtime but every `authService.hasPermission('ng.orders', …)`
 * call is a compile error. `docs/permissions.md` is the walkthrough.
 *
 * The Price Hub screens (Market Data) use this seam for their two resources:
 * `ng.prices` (view, correct, register) and `ng.price_sources` (view, manage). PHS still
 * enforces its own `phs.*` permissions server-side; these gate the frontend.
 * Route/nav tuples and the exported `*_PERMISSION` consts below are the shape
 * of every consumer call. `docs/permissions.md` is the walkthrough.
 */
import type { AuthResourceActionsCatalog, PermissionTuple } from '@trueform/ui/core';

/**
 * Half #1 (types): the open catalogue interface from
 * `@trueform/ui/core` (`auth-permissions.ts`), widened with this app's
 * resources. Each feature screen family carries its own per-feature resource
 * (ADR-018 D1). The backend still enforces its own grants server-side.
 */
declare module '@trueform/ui/core' {
  interface TrueformResourceActions {
    'ng.prices': {
      view: import('@trueform/ui/core').AuthActionConfig;
      correct: import('@trueform/ui/core').AuthActionConfig;
      register: import('@trueform/ui/core').AuthActionConfig;
      delete: import('@trueform/ui/core').AuthActionConfig;
    };
    'ng.price_sources': {
      view: import('@trueform/ui/core').AuthActionConfig;
      manage: import('@trueform/ui/core').AuthActionConfig;
    };
    // FX Rates (PHS, Market Data sibling of `ng.prices`). The screen family is
    // the same register/correct/delete sheet set, so the actions mirror
    // `ng.prices` exactly (ARCH-008 D5). PHS still enforces its own `phs.*`
    // grants server-side; these gate the frontend.
    'ng.fx_rates': {
      view: import('@trueform/ui/core').AuthActionConfig;
      register: import('@trueform/ui/core').AuthActionConfig;
      correct: import('@trueform/ui/core').AuthActionConfig;
      delete: import('@trueform/ui/core').AuthActionConfig;
    };
    // Lite Calculator (PCS-lite calculation stepper, DESIGN-004 §10 / D-16):
    // gates the two-step calculation screen. PCS still enforces its own `pcs.*`
    // grants server-side; these gate the frontend.
    'ng.pcs_calculator': {
      view: import('@trueform/ui/core').AuthActionConfig;
      calculate: import('@trueform/ui/core').AuthActionConfig;
      save: import('@trueform/ui/core').AuthActionConfig;
      load: import('@trueform/ui/core').AuthActionConfig;
      delete: import('@trueform/ui/core').AuthActionConfig;
      export: import('@trueform/ui/core').AuthActionConfig;
    };
    // Countries catalog (WUIAM-21): the FIRST PER-FEATURE granular resource
    // (ADR-018 D1) — one resource per screen family, named after the wire
    // entity (`ng.<snake_plural_entity>`), with an action only where an
    // affordance exists. Informational backend correspondence (never encoded by
    // the app): `dcs.country:{read,create,edit,delete,history}`; DCS still
    // enforces its own `dcs.*` grants server-side.
    //
    // When adding the next granular resource (WUIAM-22's `ng.tariff_duty_factors`
    // is next): gate the ROUTE LEAF with its `view` tuple (never the section
    // parent) and the NAV CHILD with the same tuple. A section-level gate
    // re-creates the defeated-granularity bug documented at `app.routes.ts` —
    // already fixed for `/catalogs` and `/price-calculator`.
    'ng.countries': {
      view: import('@trueform/ui/core').AuthActionConfig;
      create: import('@trueform/ui/core').AuthActionConfig;
      edit: import('@trueform/ui/core').AuthActionConfig;
      delete: import('@trueform/ui/core').AuthActionConfig;
      history: import('@trueform/ui/core').AuthActionConfig;
    };
    // Tariff-duty factors (PCS): a per-feature, action-exact resource
    // (ADR-018 D1). `view` gates the route + nav child; `create`/`edit`/
    // `delete` gate the kit toolbar/row actions. There is NO `history` action
    // (no history endpoint exists for this aggregate) and no `list` action
    // distinct from `view` (one screen = list+read).
    //
    // App↔backend correspondence (documented, never enforced — the app never
    // encodes `pcs.*`): view ↔ `pcs.tariff-duty-factors:list` + `:read`,
    // create ↔ `:create`, edit ↔ `:update`, delete ↔ `:delete`
    // (PCS spec L1183-1188). PCS still enforces those server-side; these gate
    // the frontend.
    'ng.tariff_duty_factors': {
      view: import('@trueform/ui/core').AuthActionConfig;
      create: import('@trueform/ui/core').AuthActionConfig;
      edit: import('@trueform/ui/core').AuthActionConfig;
      delete: import('@trueform/ui/core').AuthActionConfig;
    };
    // Terminal-expense rates (PCS): a per-feature, action-exact resource
    // (ADR-018 D1). `view` gates the route + nav child; `create`/`edit`/
    // `delete` gate the kit toolbar/row actions. There is NO `history` action
    // (no history endpoint exists for this aggregate) and no `list` action
    // distinct from `view` (one screen = list+read).
    //
    // App↔backend correspondence (documented, never enforced — the app never
    // encodes `pcs.*`): view ↔ `pcs.terminal-expenses:list` + `:read`,
    // create ↔ `:create`, edit ↔ `:update`, delete ↔ `:delete`
    // (PCS spec L1210-1220). PCS still enforces those server-side; these gate
    // the frontend.
    'ng.terminal_expenses': {
      view: import('@trueform/ui/core').AuthActionConfig;
      create: import('@trueform/ui/core').AuthActionConfig;
      edit: import('@trueform/ui/core').AuthActionConfig;
      delete: import('@trueform/ui/core').AuthActionConfig;
    };
    // DXV band configurations (PCS): a per-feature, action-exact resource
    // (ADR-018 D1). `view` gates the route + nav child; `create`/`edit`/
    // `delete` gate the kit toolbar/row actions. There is NO `history` action
    // (audit is backend-internal with no HTTP history endpoint — ARCH-024 §1),
    // no `list` action distinct from `view` (one screen = list+read), and no
    // `effective` action (the effective lookup is the calculator's read path,
    // not an admin affordance — ARCH-024 §5-L).
    //
    // App↔backend correspondence (documented, never enforced — the app never
    // encodes `pcs.*`): view ↔ `pcs.dxv-configurations:list` + `:read`,
    // create ↔ `:create`, edit ↔ `:update`, delete ↔ `:delete`
    // (PCS spec L1235-1242). PCS still enforces those server-side; these gate
    // the frontend.
    'ng.dxv_configurations': {
      view: import('@trueform/ui/core').AuthActionConfig;
      create: import('@trueform/ui/core').AuthActionConfig;
      edit: import('@trueform/ui/core').AuthActionConfig;
      delete: import('@trueform/ui/core').AuthActionConfig;
    };
    // IVA rates (PCS): a per-feature, action-exact resource (ADR-018 D1).
    // `view` gates the route + nav child; `create`/`edit`/`delete` gate the kit
    // toolbar/row actions. There is NO `history` action (delete is physical
    // with a final audit snapshot — no HTTP history endpoint exists for this
    // aggregate, ARCH-035 §2a), no `list` action distinct from `view` (one
    // screen = list+read), and no `latest`/`effective` action (the
    // current-effective-rate lookup is the screen's own read path, covered by
    // `view` — ARCH-035 §4-B).
    //
    // App↔backend correspondence (documented, never enforced — the app never
    // encodes `pcs.*`): view ↔ `pcs.iva-rates:list` + `:read`,
    // create ↔ `:create`, edit ↔ `:update`, delete ↔ `:delete`
    // (PCS spec L1400-1409). PCS still enforces those server-side; these gate
    // the frontend.
    'ng.iva_rates': {
      view: import('@trueform/ui/core').AuthActionConfig;
      create: import('@trueform/ui/core').AuthActionConfig;
      edit: import('@trueform/ui/core').AuthActionConfig;
      delete: import('@trueform/ui/core').AuthActionConfig;
    };

    /* ======================================================================
     * WUIAM-57 (TASK-472 / ADR-018 / ADR-025): Granular Retrofit Resources
     * ====================================================================== */
    // Price Calculator tariff screens: 4 CRUD actions each (ODQ-6).
    'ng.ddu_tariffs': {
      view: import('@trueform/ui/core').AuthActionConfig;
      create: import('@trueform/ui/core').AuthActionConfig;
      edit: import('@trueform/ui/core').AuthActionConfig;
      delete: import('@trueform/ui/core').AuthActionConfig;
    };
    'ng.freight_tariffs': {
      view: import('@trueform/ui/core').AuthActionConfig;
      create: import('@trueform/ui/core').AuthActionConfig;
      edit: import('@trueform/ui/core').AuthActionConfig;
      delete: import('@trueform/ui/core').AuthActionConfig;
    };
    'ng.import_taxes': {
      view: import('@trueform/ui/core').AuthActionConfig;
      create: import('@trueform/ui/core').AuthActionConfig;
      edit: import('@trueform/ui/core').AuthActionConfig;
      delete: import('@trueform/ui/core').AuthActionConfig;
    };
    'ng.packaging_tariffs': {
      view: import('@trueform/ui/core').AuthActionConfig;
      create: import('@trueform/ui/core').AuthActionConfig;
      edit: import('@trueform/ui/core').AuthActionConfig;
      delete: import('@trueform/ui/core').AuthActionConfig;
    };
    'ng.payment_term_charges': {
      view: import('@trueform/ui/core').AuthActionConfig;
      create: import('@trueform/ui/core').AuthActionConfig;
      edit: import('@trueform/ui/core').AuthActionConfig;
      delete: import('@trueform/ui/core').AuthActionConfig;
    };

    // Catalogs screens:
    'ng.products': {
      view: import('@trueform/ui/core').AuthActionConfig;
      create: import('@trueform/ui/core').AuthActionConfig;
      edit: import('@trueform/ui/core').AuthActionConfig;
      delete: import('@trueform/ui/core').AuthActionConfig;
      activate: import('@trueform/ui/core').AuthActionConfig;
      report: import('@trueform/ui/core').AuthActionConfig;
      import: import('@trueform/ui/core').AuthActionConfig;
    };
    'ng.suppliers': {
      view: import('@trueform/ui/core').AuthActionConfig;
      create: import('@trueform/ui/core').AuthActionConfig;
      edit: import('@trueform/ui/core').AuthActionConfig;
      delete: import('@trueform/ui/core').AuthActionConfig;
      activate: import('@trueform/ui/core').AuthActionConfig;
      report: import('@trueform/ui/core').AuthActionConfig;
    };
    'ng.supplier_groups': {
      view: import('@trueform/ui/core').AuthActionConfig;
      create: import('@trueform/ui/core').AuthActionConfig;
      edit: import('@trueform/ui/core').AuthActionConfig;
      delete: import('@trueform/ui/core').AuthActionConfig;
      activate: import('@trueform/ui/core').AuthActionConfig;
      report: import('@trueform/ui/core').AuthActionConfig;
    };
    // Currencies (ODQ-2: CRUD only, history stays on view).
    'ng.currencies': {
      view: import('@trueform/ui/core').AuthActionConfig;
      create: import('@trueform/ui/core').AuthActionConfig;
      edit: import('@trueform/ui/core').AuthActionConfig;
      delete: import('@trueform/ui/core').AuthActionConfig;
    };
    'ng.hubs': {
      view: import('@trueform/ui/core').AuthActionConfig;
      create: import('@trueform/ui/core').AuthActionConfig;
      edit: import('@trueform/ui/core').AuthActionConfig;
      delete: import('@trueform/ui/core').AuthActionConfig;
      activate: import('@trueform/ui/core').AuthActionConfig;
      report: import('@trueform/ui/core').AuthActionConfig;
    };
    'ng.destination_cities': {
      view: import('@trueform/ui/core').AuthActionConfig;
      create: import('@trueform/ui/core').AuthActionConfig;
      edit: import('@trueform/ui/core').AuthActionConfig;
      delete: import('@trueform/ui/core').AuthActionConfig;
      activate: import('@trueform/ui/core').AuthActionConfig;
      report: import('@trueform/ui/core').AuthActionConfig;
    };
    'ng.external_contexts': {
      view: import('@trueform/ui/core').AuthActionConfig;
      create: import('@trueform/ui/core').AuthActionConfig;
      edit: import('@trueform/ui/core').AuthActionConfig;
      delete: import('@trueform/ui/core').AuthActionConfig;
      activate: import('@trueform/ui/core').AuthActionConfig;
      report: import('@trueform/ui/core').AuthActionConfig;
    };
    'ng.transport_methods': {
      view: import('@trueform/ui/core').AuthActionConfig;
      create: import('@trueform/ui/core').AuthActionConfig;
      edit: import('@trueform/ui/core').AuthActionConfig;
      delete: import('@trueform/ui/core').AuthActionConfig;
      activate: import('@trueform/ui/core').AuthActionConfig;
      report: import('@trueform/ui/core').AuthActionConfig;
    };
    'ng.payment_terms': {
      view: import('@trueform/ui/core').AuthActionConfig;
      create: import('@trueform/ui/core').AuthActionConfig;
      edit: import('@trueform/ui/core').AuthActionConfig;
      delete: import('@trueform/ui/core').AuthActionConfig;
      activate: import('@trueform/ui/core').AuthActionConfig;
      report: import('@trueform/ui/core').AuthActionConfig;
    };
    // Packagings catalog (WUIAM-130 / DCS 0.4.0 / ADR-018 D1). The 11th
    // company-scoped catalog root, action-exact: `view` gates the route + nav
    // child, `create`/`edit`/`delete`/`activate` gate the kit affordances,
    // `report` gates the reporting list, and `history` exists because the
    // aggregate HAS a history endpoint (spec §Packaging) — unlike the PCS
    // reference screens, which own no history action.
    //
    // App↔backend correspondence (documented, never enforced — the app never
    // encodes `dcs.*`): view ↔ `dcs.packaging:read`, create ↔ `:create`,
    // edit ↔ `:edit`, delete ↔ `:delete`, activate ↔ `:activate`,
    // report ↔ `:report`, history ↔ `:history`. DCS still enforces those
    // server-side; these gate the frontend.
    'ng.packagings': {
      view: import('@trueform/ui/core').AuthActionConfig;
      create: import('@trueform/ui/core').AuthActionConfig;
      edit: import('@trueform/ui/core').AuthActionConfig;
      delete: import('@trueform/ui/core').AuthActionConfig;
      activate: import('@trueform/ui/core').AuthActionConfig;
      report: import('@trueform/ui/core').AuthActionConfig;
      history: import('@trueform/ui/core').AuthActionConfig;
    };
  }
}

/**
 * Half #2 (runtime): this app's own resource→action catalogue. It MUST carry
 * exactly the resources augmented above — the `satisfies` clause enforces the
 * `ng.` prefix and the `{ description }` shape; keep the two blocks in sync.
 * `provideAuthCatalog()` merges it OVER the library baseline in
 * `app.config.ts` (same-key resources are replaced, new ones added), so the
 * runtime list of assignable permissions includes `ng.products:view`, …
 */
export const APP_RESOURCE_ACTIONS = {
  'ng.prices': {
    view: { description: 'View and query prices in the Price Hub.' },
    correct: { description: 'Correct an existing price.' },
    register: { description: 'Register a new price for a product and source.' },
    delete: { description: 'Delete a price.' },
  },
  'ng.price_sources': {
    view: { description: 'View price sources.' },
    manage: { description: 'Create, rename or delete price sources.' },
  },
  'ng.fx_rates': {
    view: { description: 'View and query FX rates in the Market Data screens.' },
    register: { description: 'Register a new FX rate for a currency pair and source.' },
    correct: { description: 'Correct the rate of an existing FX rate.' },
    delete: { description: 'Delete an FX rate.' },
  },
  'ng.pcs_calculator': {
    view: { description: 'View the Lite Calculator screens in the frontend (route and nav).' },
    calculate: {
      description: 'Add or edit materials and calculate prices in the Lite Calculator.',
    },
    save: {
      description: 'Save a calculation as a new configuration or replace the loaded one.',
    },
    load: {
      description: 'List and load saved calculation configurations in the Lite Calculator.',
    },
    delete: {
      description: 'Delete a saved calculation configuration in the Lite Calculator.',
    },
    export: {
      description:
        'Download the reviewed Lite Calculator result as PDF or Excel. Backend authorization (pcs.calculations:export) stays authoritative.',
    },
  },
  // Countries catalog (WUIAM-21 / ADR-018 D1). Descriptions are English-only
  // config strings (the roles-picker meta), NOT i18n. The backend analog of
  // each action is informational only: view → dcs.country:read,
  // create → dcs.country:create, edit → dcs.country:edit,
  // delete → dcs.country:delete, history → dcs.country:history.
  'ng.countries': {
    view: { description: 'View the Countries catalog screen in the frontend.' },
    create: { description: 'Create a country in the frontend.' },
    edit: { description: 'Edit a country in the frontend.' },
    delete: { description: 'Delete a country in the frontend.' },
    history: { description: "View a country's audit history in the frontend." },
  },
  // Tariff-duty factors (PCS): per-feature resource (ADR-018 D1), mirroring
  // the augmentation above. Backend correspondence is informational only:
  // view ↔ `pcs.tariff-duty-factors:list` + `:read`, create ↔ `:create`,
  // edit ↔ `:update`, delete ↔ `:delete`.
  'ng.tariff_duty_factors': {
    view: { description: 'View the Tariff-duty factors screen in the frontend.' },
    create: { description: 'Create a tariff-duty factor in the frontend.' },
    edit: { description: 'Edit a tariff-duty factor in the frontend.' },
    delete: { description: 'Delete a tariff-duty factor in the frontend.' },
  },
  // Terminal-expense rates (PCS): per-feature resource (ADR-018 D1), mirroring
  // the augmentation above. Backend correspondence is informational only:
  // view ↔ `pcs.terminal-expenses:list` + `:read`, create ↔ `:create`,
  // edit ↔ `:update`, delete ↔ `:delete`.
  'ng.terminal_expenses': {
    view: { description: 'View the Terminal expenses screen in the frontend.' },
    create: { description: 'Create a terminal expense in the frontend.' },
    edit: { description: 'Edit a terminal expense in the frontend.' },
    delete: { description: 'Delete a terminal expense in the frontend.' },
  },
  // DXV band configurations (PCS): per-feature resource (ADR-018 D1),
  // mirroring the augmentation above. Backend correspondence is informational
  // only: view ↔ `pcs.dxv-configurations:list` + `:read`, create ↔ `:create`,
  // edit ↔ `:update`, delete ↔ `:delete`.
  'ng.dxv_configurations': {
    view: { description: 'View the DXV band configurations screen in the frontend.' },
    create: { description: 'Create a DXV band configuration in the frontend.' },
    edit: { description: 'Edit a DXV band configuration in the frontend.' },
    delete: { description: 'Delete a DXV band configuration in the frontend.' },
  },
  // IVA rates (PCS): per-feature resource (ADR-018 D1), mirroring the
  // augmentation above. Backend correspondence is informational only:
  // view ↔ `pcs.iva-rates:list` + `:read`, create ↔ `:create`,
  // edit ↔ `:update`, delete ↔ `:delete`.
  'ng.iva_rates': {
    view: { description: 'View the IVA rates screen in the frontend.' },
    create: { description: 'Create an IVA rate in the frontend.' },
    edit: { description: 'Edit an IVA rate in the frontend.' },
    delete: { description: 'Delete an IVA rate in the frontend.' },
  },

  /* WUIAM-57 Price Calculator Granular Resources */
  'ng.ddu_tariffs': {
    view: { description: 'View the DDU Tariffs screen in the frontend.' },
    create: { description: 'Create a DDU tariff in the frontend.' },
    edit: { description: 'Edit a DDU tariff in the frontend.' },
    delete: { description: 'Delete a DDU tariff in the frontend.' },
  },
  'ng.freight_tariffs': {
    view: { description: 'View the Freight Tariffs screen in the frontend.' },
    create: { description: 'Create a freight tariff in the frontend.' },
    edit: { description: 'Edit a freight tariff in the frontend.' },
    delete: { description: 'Delete a freight tariff in the frontend.' },
  },
  'ng.import_taxes': {
    view: { description: 'View the Import Taxes screen in the frontend.' },
    create: { description: 'Create an import tax in the frontend.' },
    edit: { description: 'Edit an import tax in the frontend.' },
    delete: { description: 'Delete an import tax in the frontend.' },
  },
  // Packaging tariffs (PCS, WUIAM-132): per-feature resource (ADR-018 D1).
  // Backend correspondence is informational only: view ↔
  // `pcs.packaging-tariffs:list` + `:read`, create ↔ `:create`,
  // edit ↔ `:update`, delete ↔ `:delete`.
  'ng.packaging_tariffs': {
    view: { description: 'View the Packaging Tariffs screen in the frontend.' },
    create: { description: 'Create a packaging tariff in the frontend.' },
    edit: { description: 'Edit a packaging tariff in the frontend.' },
    delete: { description: 'Delete a packaging tariff in the frontend.' },
  },
  'ng.payment_term_charges': {
    view: { description: 'View the Payment Rates screen in the frontend.' },
    create: { description: 'Create a payment-term charge in the frontend.' },
    edit: { description: 'Edit a payment-term charge in the frontend.' },
    delete: { description: 'Delete a payment-term charge in the frontend.' },
  },

  /* WUIAM-57 Catalogs Granular Resources */
  'ng.products': {
    view: { description: 'View the Products catalog screen in the frontend.' },
    create: { description: 'Create a product in the frontend.' },
    edit: { description: 'Edit a product in the frontend.' },
    delete: { description: 'Delete a product in the frontend.' },
    activate: { description: 'Activate or deactivate a product in the frontend.' },
    report: { description: 'View operational and reporting product lists in the frontend.' },
    import: { description: 'Import products from SAP in the frontend.' },
  },
  'ng.suppliers': {
    view: { description: 'View the Suppliers catalog screen in the frontend.' },
    create: { description: 'Create a supplier in the frontend.' },
    edit: { description: 'Edit a supplier in the frontend.' },
    delete: { description: 'Delete a supplier in the frontend.' },
    activate: { description: 'Activate or deactivate a supplier in the frontend.' },
    report: { description: 'View operational and reporting supplier lists in the frontend.' },
  },
  'ng.supplier_groups': {
    view: { description: 'View the Supplier groups catalog screen in the frontend.' },
    create: { description: 'Create a supplier group in the frontend.' },
    edit: { description: 'Edit a supplier group in the frontend.' },
    delete: { description: 'Delete a supplier group in the frontend.' },
    activate: { description: 'Activate or deactivate a supplier group in the frontend.' },
    report: { description: 'View operational and reporting supplier group lists in the frontend.' },
  },
  'ng.currencies': {
    view: {
      description:
        'View the Currencies catalog screen (including its audit history) in the frontend.',
    },
    create: { description: 'Create a currency in the frontend.' },
    edit: { description: 'Edit a currency in the frontend.' },
    delete: { description: 'Delete a currency in the frontend.' },
  },
  'ng.hubs': {
    view: { description: 'View the Hubs catalog screen in the frontend.' },
    create: { description: 'Create a hub in the frontend.' },
    edit: { description: 'Edit a hub in the frontend.' },
    delete: { description: 'Delete a hub in the frontend.' },
    activate: { description: 'Activate or deactivate a hub in the frontend.' },
    report: { description: 'View operational and reporting hub lists in the frontend.' },
  },
  'ng.destination_cities': {
    view: { description: 'View the Destination cities catalog screen in the frontend.' },
    create: { description: 'Create a destination city in the frontend.' },
    edit: { description: 'Edit a destination city in the frontend.' },
    delete: { description: 'Delete a destination city in the frontend.' },
    activate: { description: 'Activate or deactivate a destination city in the frontend.' },
    report: {
      description: 'View operational and reporting destination city lists in the frontend.',
    },
  },
  'ng.external_contexts': {
    view: { description: 'View the External contexts catalog screen in the frontend.' },
    create: { description: 'Create an external context in the frontend.' },
    edit: { description: 'Edit an external context in the frontend.' },
    delete: { description: 'Delete an external context in the frontend.' },
    activate: { description: 'Activate or deactivate an external context in the frontend.' },
    report: {
      description: 'View operational and reporting external context lists in the frontend.',
    },
  },
  'ng.transport_methods': {
    view: { description: 'View the Transport methods catalog screen in the frontend.' },
    create: { description: 'Create a transport method in the frontend.' },
    edit: { description: 'Edit a transport method in the frontend.' },
    delete: { description: 'Delete a transport method in the frontend.' },
    activate: { description: 'Activate or deactivate a transport method in the frontend.' },
    report: {
      description: 'View operational and reporting transport method lists in the frontend.',
    },
  },
  'ng.payment_terms': {
    view: { description: 'View the Payment terms catalog screen in the frontend.' },
    create: { description: 'Create a payment term in the frontend.' },
    edit: { description: 'Edit a payment term in the frontend.' },
    delete: { description: 'Delete a payment term in the frontend.' },
    activate: { description: 'Activate or deactivate a payment term in the frontend.' },
    report: { description: 'View operational and reporting payment term lists in the frontend.' },
  },
  'ng.packagings': {
    view: { description: 'View the Packagings catalog screen in the frontend.' },
    create: { description: 'Create a packaging in the frontend.' },
    edit: { description: 'Edit a packaging in the frontend.' },
    delete: { description: 'Delete a packaging in the frontend.' },
    activate: { description: 'Activate or deactivate a packaging in the frontend.' },
    report: { description: 'View operational and reporting packaging lists in the frontend.' },
    history: { description: "View a packaging's audit history in the frontend." },
  },
} as const satisfies AuthResourceActionsCatalog;

// The compile-time proof that half #1 actually did something: these tuples
// type-check only while 'ng.prices' / 'ng.price_sources' are augmented into
// TrueformResourceActions. They are the shape of every route
// `data.permission`, nav-section `permission`, and
// authService.hasPermission(...) call this app writes for the Market Data
// screens.
export const PRICES_VIEW_PERMISSION: import('@trueform/ui/core').PermissionTuple = [
  'ng.prices',
  'view',
];
export const PRICES_CORRECT_PERMISSION: import('@trueform/ui/core').PermissionTuple = [
  'ng.prices',
  'correct',
];
export const PRICES_REGISTER_PERMISSION: import('@trueform/ui/core').PermissionTuple = [
  'ng.prices',
  'register',
];
export const PRICES_DELETE_PERMISSION: import('@trueform/ui/core').PermissionTuple = [
  'ng.prices',
  'delete',
];
export const PRICE_SOURCES_VIEW_PERMISSION: import('@trueform/ui/core').PermissionTuple = [
  'ng.price_sources',
  'view',
];
export const PRICE_SOURCES_MANAGE_PERMISSION: import('@trueform/ui/core').PermissionTuple = [
  'ng.price_sources',
  'manage',
];
export const FX_RATES_VIEW_PERMISSION: import('@trueform/ui/core').PermissionTuple = [
  'ng.fx_rates',
  'view',
];
export const FX_RATES_REGISTER_PERMISSION: import('@trueform/ui/core').PermissionTuple = [
  'ng.fx_rates',
  'register',
];
export const FX_RATES_CORRECT_PERMISSION: import('@trueform/ui/core').PermissionTuple = [
  'ng.fx_rates',
  'correct',
];
export const FX_RATES_DELETE_PERMISSION: import('@trueform/ui/core').PermissionTuple = [
  'ng.fx_rates',
  'delete',
];

export const PCS_CALCULATOR_VIEW_PERMISSION: PermissionTuple = ['ng.pcs_calculator', 'view'];
export const PCS_CALCULATOR_CALCULATE_PERMISSION: PermissionTuple = [
  'ng.pcs_calculator',
  'calculate',
];
export const PCS_CALCULATOR_SAVE_PERMISSION: PermissionTuple = ['ng.pcs_calculator', 'save'];
export const PCS_CALCULATOR_LOAD_PERMISSION: PermissionTuple = ['ng.pcs_calculator', 'load'];
export const PCS_CALCULATOR_DELETE_PERMISSION: PermissionTuple = ['ng.pcs_calculator', 'delete'];
export const PCS_CALCULATOR_EXPORT_PERMISSION: PermissionTuple = ['ng.pcs_calculator', 'export'];

// Countries (WUIAM-21 / ADR-018 D1) — the first per-feature tuples. Backend
// correspondence (informational): view/create/edit/delete/history →
// dcs.country:{read,create,edit,delete,history}.
export const COUNTRIES_VIEW_PERMISSION: PermissionTuple = ['ng.countries', 'view'];
export const COUNTRIES_CREATE_PERMISSION: PermissionTuple = ['ng.countries', 'create'];
export const COUNTRIES_EDIT_PERMISSION: PermissionTuple = ['ng.countries', 'edit'];
export const COUNTRIES_DELETE_PERMISSION: PermissionTuple = ['ng.countries', 'delete'];
export const COUNTRIES_HISTORY_PERMISSION: PermissionTuple = ['ng.countries', 'history'];

// Tariff-duty factors (WUIAM-22 / ADR-018 D1): the per-feature tuples consumed
// by the route `data.permission`, the nav child and the kit
// `actionPermissions` (TASK-314/TASK-313).
export const TARIFF_DUTY_FACTORS_VIEW_PERMISSION: PermissionTuple = [
  'ng.tariff_duty_factors',
  'view',
];
export const TARIFF_DUTY_FACTORS_CREATE_PERMISSION: PermissionTuple = [
  'ng.tariff_duty_factors',
  'create',
];
export const TARIFF_DUTY_FACTORS_EDIT_PERMISSION: PermissionTuple = [
  'ng.tariff_duty_factors',
  'edit',
];
export const TARIFF_DUTY_FACTORS_DELETE_PERMISSION: PermissionTuple = [
  'ng.tariff_duty_factors',
  'delete',
];

// Terminal-expense rates (WUIAM-36 / ADR-018 D1): the per-feature tuples
// consumed by the route `data.permission`, the nav child and the kit
// `actionPermissions` (TASK-335/TASK-336). Backend correspondence
// (informational): view ↔ `pcs.terminal-expenses:list` + `:read`,
// create ↔ `:create`, edit ↔ `:update`, delete ↔ `:delete`.
export const TERMINAL_EXPENSES_VIEW_PERMISSION: PermissionTuple = ['ng.terminal_expenses', 'view'];
export const TERMINAL_EXPENSES_CREATE_PERMISSION: PermissionTuple = [
  'ng.terminal_expenses',
  'create',
];
export const TERMINAL_EXPENSES_EDIT_PERMISSION: PermissionTuple = ['ng.terminal_expenses', 'edit'];
export const TERMINAL_EXPENSES_DELETE_PERMISSION: PermissionTuple = [
  'ng.terminal_expenses',
  'delete',
];
// DXV band configurations (WUIAM-23 / ADR-018 D1): the per-feature tuples
// consumed by the route `data.permission`, the nav child and the kit
// `actionPermissions` (TASK-356). Backend correspondence (informational):
// view/create/edit/delete → pcs.dxv-configurations:{list+read,create,update,delete}.
export const DXV_CONFIGURATIONS_VIEW_PERMISSION: PermissionTuple = [
  'ng.dxv_configurations',
  'view',
];
export const DXV_CONFIGURATIONS_CREATE_PERMISSION: PermissionTuple = [
  'ng.dxv_configurations',
  'create',
];
export const DXV_CONFIGURATIONS_EDIT_PERMISSION: PermissionTuple = [
  'ng.dxv_configurations',
  'edit',
];
export const DXV_CONFIGURATIONS_DELETE_PERMISSION: PermissionTuple = [
  'ng.dxv_configurations',
  'delete',
];

// IVA rates (WUIAM-111 / ADR-018 D1): the per-feature tuples consumed by the
// route `data.permission`, the nav child and the kit `actionPermissions`
// (TASK-498; the kit screen consumes create/edit/delete in WUIAM-111-B).
// Backend correspondence (informational): view ↔ `pcs.iva-rates:list` + `:read`,
// create ↔ `:create`, edit ↔ `:update`, delete ↔ `:delete`.
export const IVA_RATES_VIEW_PERMISSION: PermissionTuple = ['ng.iva_rates', 'view'];
export const IVA_RATES_CREATE_PERMISSION: PermissionTuple = ['ng.iva_rates', 'create'];
export const IVA_RATES_EDIT_PERMISSION: PermissionTuple = ['ng.iva_rates', 'edit'];
export const IVA_RATES_DELETE_PERMISSION: PermissionTuple = ['ng.iva_rates', 'delete'];

/* ==========================================================================
 * WUIAM-57 Granular Tuples (AC-2)
 * ========================================================================== */

// Price Calculator: 5 resources
export const DDU_TARIFFS_VIEW_PERMISSION: PermissionTuple = ['ng.ddu_tariffs', 'view'];
export const DDU_TARIFFS_CREATE_PERMISSION: PermissionTuple = ['ng.ddu_tariffs', 'create'];
export const DDU_TARIFFS_EDIT_PERMISSION: PermissionTuple = ['ng.ddu_tariffs', 'edit'];
export const DDU_TARIFFS_DELETE_PERMISSION: PermissionTuple = ['ng.ddu_tariffs', 'delete'];

export const FREIGHT_TARIFFS_VIEW_PERMISSION: PermissionTuple = ['ng.freight_tariffs', 'view'];
export const FREIGHT_TARIFFS_CREATE_PERMISSION: PermissionTuple = ['ng.freight_tariffs', 'create'];
export const FREIGHT_TARIFFS_EDIT_PERMISSION: PermissionTuple = ['ng.freight_tariffs', 'edit'];
export const FREIGHT_TARIFFS_DELETE_PERMISSION: PermissionTuple = ['ng.freight_tariffs', 'delete'];

export const IMPORT_TAXES_VIEW_PERMISSION: PermissionTuple = ['ng.import_taxes', 'view'];
export const IMPORT_TAXES_CREATE_PERMISSION: PermissionTuple = ['ng.import_taxes', 'create'];
export const IMPORT_TAXES_EDIT_PERMISSION: PermissionTuple = ['ng.import_taxes', 'edit'];
export const IMPORT_TAXES_DELETE_PERMISSION: PermissionTuple = ['ng.import_taxes', 'delete'];

export const PACKAGING_TARIFFS_VIEW_PERMISSION: PermissionTuple = ['ng.packaging_tariffs', 'view'];
export const PACKAGING_TARIFFS_CREATE_PERMISSION: PermissionTuple = [
  'ng.packaging_tariffs',
  'create',
];
export const PACKAGING_TARIFFS_EDIT_PERMISSION: PermissionTuple = ['ng.packaging_tariffs', 'edit'];
export const PACKAGING_TARIFFS_DELETE_PERMISSION: PermissionTuple = [
  'ng.packaging_tariffs',
  'delete',
];

// PCS "Payment Rates" tariff screen (ng.payment_term_charges) — NOT the DCS Payment terms catalog
export const PAYMENT_TERM_CHARGES_VIEW_PERMISSION: PermissionTuple = [
  'ng.payment_term_charges',
  'view',
];
export const PAYMENT_TERM_CHARGES_CREATE_PERMISSION: PermissionTuple = [
  'ng.payment_term_charges',
  'create',
];
export const PAYMENT_TERM_CHARGES_EDIT_PERMISSION: PermissionTuple = [
  'ng.payment_term_charges',
  'edit',
];
export const PAYMENT_TERM_CHARGES_DELETE_PERMISSION: PermissionTuple = [
  'ng.payment_term_charges',
  'delete',
];

// Catalogs: 9 resources
export const PRODUCTS_VIEW_PERMISSION: PermissionTuple = ['ng.products', 'view'];
export const PRODUCTS_CREATE_PERMISSION: PermissionTuple = ['ng.products', 'create'];
export const PRODUCTS_EDIT_PERMISSION: PermissionTuple = ['ng.products', 'edit'];
export const PRODUCTS_DELETE_PERMISSION: PermissionTuple = ['ng.products', 'delete'];
export const PRODUCTS_ACTIVATE_PERMISSION: PermissionTuple = ['ng.products', 'activate'];
export const PRODUCTS_REPORT_PERMISSION: PermissionTuple = ['ng.products', 'report'];
export const PRODUCTS_IMPORT_PERMISSION: PermissionTuple = ['ng.products', 'import'];

export const SUPPLIERS_VIEW_PERMISSION: PermissionTuple = ['ng.suppliers', 'view'];
export const SUPPLIERS_CREATE_PERMISSION: PermissionTuple = ['ng.suppliers', 'create'];
export const SUPPLIERS_EDIT_PERMISSION: PermissionTuple = ['ng.suppliers', 'edit'];
export const SUPPLIERS_DELETE_PERMISSION: PermissionTuple = ['ng.suppliers', 'delete'];
export const SUPPLIERS_ACTIVATE_PERMISSION: PermissionTuple = ['ng.suppliers', 'activate'];
export const SUPPLIERS_REPORT_PERMISSION: PermissionTuple = ['ng.suppliers', 'report'];

export const SUPPLIER_GROUPS_VIEW_PERMISSION: PermissionTuple = ['ng.supplier_groups', 'view'];
export const SUPPLIER_GROUPS_CREATE_PERMISSION: PermissionTuple = ['ng.supplier_groups', 'create'];
export const SUPPLIER_GROUPS_EDIT_PERMISSION: PermissionTuple = ['ng.supplier_groups', 'edit'];
export const SUPPLIER_GROUPS_DELETE_PERMISSION: PermissionTuple = ['ng.supplier_groups', 'delete'];
export const SUPPLIER_GROUPS_ACTIVATE_PERMISSION: PermissionTuple = [
  'ng.supplier_groups',
  'activate',
];
export const SUPPLIER_GROUPS_REPORT_PERMISSION: PermissionTuple = ['ng.supplier_groups', 'report'];

export const CURRENCIES_VIEW_PERMISSION: PermissionTuple = ['ng.currencies', 'view'];
export const CURRENCIES_CREATE_PERMISSION: PermissionTuple = ['ng.currencies', 'create'];
export const CURRENCIES_EDIT_PERMISSION: PermissionTuple = ['ng.currencies', 'edit'];
export const CURRENCIES_DELETE_PERMISSION: PermissionTuple = ['ng.currencies', 'delete'];

export const HUBS_VIEW_PERMISSION: PermissionTuple = ['ng.hubs', 'view'];
export const HUBS_CREATE_PERMISSION: PermissionTuple = ['ng.hubs', 'create'];
export const HUBS_EDIT_PERMISSION: PermissionTuple = ['ng.hubs', 'edit'];
export const HUBS_DELETE_PERMISSION: PermissionTuple = ['ng.hubs', 'delete'];
export const HUBS_ACTIVATE_PERMISSION: PermissionTuple = ['ng.hubs', 'activate'];
export const HUBS_REPORT_PERMISSION: PermissionTuple = ['ng.hubs', 'report'];

export const DESTINATION_CITIES_VIEW_PERMISSION: PermissionTuple = [
  'ng.destination_cities',
  'view',
];
export const DESTINATION_CITIES_CREATE_PERMISSION: PermissionTuple = [
  'ng.destination_cities',
  'create',
];
export const DESTINATION_CITIES_EDIT_PERMISSION: PermissionTuple = [
  'ng.destination_cities',
  'edit',
];
export const DESTINATION_CITIES_DELETE_PERMISSION: PermissionTuple = [
  'ng.destination_cities',
  'delete',
];
export const DESTINATION_CITIES_ACTIVATE_PERMISSION: PermissionTuple = [
  'ng.destination_cities',
  'activate',
];
export const DESTINATION_CITIES_REPORT_PERMISSION: PermissionTuple = [
  'ng.destination_cities',
  'report',
];

export const EXTERNAL_CONTEXTS_VIEW_PERMISSION: PermissionTuple = ['ng.external_contexts', 'view'];
export const EXTERNAL_CONTEXTS_CREATE_PERMISSION: PermissionTuple = [
  'ng.external_contexts',
  'create',
];
export const EXTERNAL_CONTEXTS_EDIT_PERMISSION: PermissionTuple = ['ng.external_contexts', 'edit'];
export const EXTERNAL_CONTEXTS_DELETE_PERMISSION: PermissionTuple = [
  'ng.external_contexts',
  'delete',
];
export const EXTERNAL_CONTEXTS_ACTIVATE_PERMISSION: PermissionTuple = [
  'ng.external_contexts',
  'activate',
];
export const EXTERNAL_CONTEXTS_REPORT_PERMISSION: PermissionTuple = [
  'ng.external_contexts',
  'report',
];

export const TRANSPORT_METHODS_VIEW_PERMISSION: PermissionTuple = ['ng.transport_methods', 'view'];
export const TRANSPORT_METHODS_CREATE_PERMISSION: PermissionTuple = [
  'ng.transport_methods',
  'create',
];
export const TRANSPORT_METHODS_EDIT_PERMISSION: PermissionTuple = ['ng.transport_methods', 'edit'];
export const TRANSPORT_METHODS_DELETE_PERMISSION: PermissionTuple = [
  'ng.transport_methods',
  'delete',
];
export const TRANSPORT_METHODS_ACTIVATE_PERMISSION: PermissionTuple = [
  'ng.transport_methods',
  'activate',
];
export const TRANSPORT_METHODS_REPORT_PERMISSION: PermissionTuple = [
  'ng.transport_methods',
  'report',
];

// DCS Payment terms catalog (ng.payment_terms) — see also ng.payment_term_charges above
export const PAYMENT_TERMS_VIEW_PERMISSION: PermissionTuple = ['ng.payment_terms', 'view'];
export const PAYMENT_TERMS_CREATE_PERMISSION: PermissionTuple = ['ng.payment_terms', 'create'];
export const PAYMENT_TERMS_EDIT_PERMISSION: PermissionTuple = ['ng.payment_terms', 'edit'];
export const PAYMENT_TERMS_DELETE_PERMISSION: PermissionTuple = ['ng.payment_terms', 'delete'];
export const PAYMENT_TERMS_ACTIVATE_PERMISSION: PermissionTuple = ['ng.payment_terms', 'activate'];
export const PAYMENT_TERMS_REPORT_PERMISSION: PermissionTuple = ['ng.payment_terms', 'report'];

// Packagings catalog (WUIAM-130 / DCS 0.4.0 / ADR-018 D1): the per-feature
// tuples consumed by the route `data.permission`, the nav child and the kit
// `actionPermissions` / history affordance. Backend correspondence
// (informational): view/create/edit/delete/activate/report/history →
// dcs.packaging:{read,create,edit,delete,activate,report,history}.
export const PACKAGINGS_VIEW_PERMISSION: PermissionTuple = ['ng.packagings', 'view'];
export const PACKAGINGS_CREATE_PERMISSION: PermissionTuple = ['ng.packagings', 'create'];
export const PACKAGINGS_EDIT_PERMISSION: PermissionTuple = ['ng.packagings', 'edit'];
export const PACKAGINGS_DELETE_PERMISSION: PermissionTuple = ['ng.packagings', 'delete'];
export const PACKAGINGS_ACTIVATE_PERMISSION: PermissionTuple = ['ng.packagings', 'activate'];
export const PACKAGINGS_REPORT_PERMISSION: PermissionTuple = ['ng.packagings', 'report'];
export const PACKAGINGS_HISTORY_PERMISSION: PermissionTuple = ['ng.packagings', 'history'];
